import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { TOptions } from 'i18next';
import Rating from '@material-ui/lab/Rating';
import { NativeSelect } from '@material-ui/core';
import ModalModerationPolicy from 'components/Modals/ModalModerationPolicy/ModalModerationPolicy';
import useCurrentCountry from 'hooks/useCurrentCountry';
import { useModal } from 'hooks/useModal';
import {
  ProductReviewsDetailsType,
  ProductReviewsRatingType,
} from 'types/ProductPageV2/ProductReviews';
import ProductReviewsList from './ProductReviewsList/ProductReviewsList';
import LogoReviews from 'assets/img/logo-avis-verifies.png';
import LogoReviewsMobile from 'assets/img/logo-avis-verifies-mobile.png';
import LogoReviewsUk from 'assets/img/logo-avis-verifies-uk.png';
import LogoReviewsMobileUk from 'assets/img/logo-avis-verifies-mobile-uk.png';
import styles from './ProductReviews.module.scss';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';
import { pushGtmEvent } from 'services/gtm/gtmEvents';

/**
 * Product page of website. Contains details product data.
 */
function ProductReviews({
  rating,
  detailsReviews,
  handleContentHeight,
}: {
  rating: ProductReviewsRatingType | undefined;
  detailsReviews: ProductReviewsDetailsType[] | undefined;
  handleContentHeight?: () => void;
}): JSX.Element | null {
  const { t } = useTranslation(['productPage', 'translation']);
  const { open, openModal, closeModal } = useModal();
  const [selectedFilter, setSelectedFilter] = useState<number>(1);
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });
  const { isUk } = useCurrentCountry();

  const handleSelectChange = event => {
    setSelectedFilter(parseInt(event.target.value));
  };

  useEffect(() => {
    if (typeof handleContentHeight === 'function') handleContentHeight();
  }, []);

  if (!detailsReviews || !rating) {
    return <div>{t('LABEL_REVIEWS_NO_REVIEWS')}</div>;
  }

  const filters = [
    { value: 1, label: t('LABEL_REVIEWS_FILTER_BY_LATEST') },
    { value: 2, label: t('LABEL_REVIEWS_FILTER_BY_OLDEST') },
    { value: 3, label: t('LABEL_REVIEWS_FILTER_BY_HIGHEST_TO_LOWEST') },
    { value: 4, label: t('LABEL_REVIEWS_FILTER_BY_LOWEST_TO_HIGHEST') },
  ];
  const rateRound = parseFloat(rating.rate).toFixed(1);

  const renderHeaderFilters = (): JSX.Element => {
    return (
      <div className={styles.filters}>
        <span>
          <NativeSelect onChange={handleSelectChange} defaultValue={0}>
            <option disabled value={0}>
              {t('LABEL_REVIEWS_FILTER_BY')}
            </option>
            {filters.map(filter => {
              return (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              );
            })}
          </NativeSelect>
        </span>
      </div>
    );
  };

  const renderHeaderRating = (): JSX.Element => {
    return (
      <div className={styles.rating}>
        <div className={styles.title}>
          {t('LABEL_CUSTOMERS_REVIEWS', {
            count: rating.count,
          } as TOptions<{ count: string }>)}
        </div>
        <div className={styles.rate}>
          <span>{rateRound}</span>
          <Rating
            size={'large'}
            className={styles.stars}
            value={parseFloat(rateRound)}
            precision={0.1}
            readOnly
          />
        </div>
      </div>
    );
  };

  const renderHeaderLogoLink = (): JSX.Element => {
    return (
      <div>
        <div className={styles.logo}>
          {isMobileAndPortraitTablet && (
            <img
              alt="reviews"
              className={styles.logoImg}
              src={isUk ? LogoReviewsMobileUk : LogoReviewsMobile}
            />
          )}
          {!isMobileAndPortraitTablet && (
            <img
              alt="reviews"
              className={styles.logoImg}
              src={isUk ? LogoReviewsUk : LogoReviews}
            />
          )}
        </div>
        <div className={styles.info}>
          <button
            className={styles.consultPolicy}
            onClick={() => {
              pushGtmEvent({
                event: '_click_other',
                category: 'clic autre - fiche produit',
                element: 'avis vérifies',
              });
              openModal();
            }}
          >
            {t('LABEL_RATING_POLICY', {
              ns: 'translation',
            })}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.productReviews} id="reviews">
        <div className={styles.header}>
          {isMobileAndPortraitTablet && (
            <>
              <div>{renderHeaderRating()}</div>
              <div className={styles.mobileFilters}>
                {renderHeaderFilters()}
                {renderHeaderLogoLink()}
              </div>
            </>
          )}
          {!isMobileAndPortraitTablet && (
            <>
              {renderHeaderFilters()}
              {renderHeaderRating()}
              {renderHeaderLogoLink()}
            </>
          )}
        </div>
        <ModalModerationPolicy isOpen={open} closeModal={closeModal} />
        <ProductReviewsList filter={selectedFilter} reviews={detailsReviews} />
      </div>
    </>
  );
}

export default ProductReviews;
